import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { BUCKET, FILE_ID, FILE_URL, FILE_URL_EXPIRES, MIMETYPE, NAME, ORIGINAL_NAME, OFFLINE } from 'api/consts';
import Close from 'assets/svg-icons/SmallClose';
import OfflineIcon from 'assets/svg-icons/OfflineIcon';
import Search from 'assets/svg-icons/SmallSearch';
import Checkbox from 'components/CheckboxNew';
import Warn from 'components/WarnDeleting';

import Spinner from './Spinner';
import { ButtonWrap, Container, DeleteBtn, IconWrap, Input, Name, Overlay, Preview, PreviewWrap, SmallBtn } from './styles';

const Item = ({
  file,
  id,
  status,
  readMode,
  editable,
  selectable,
  selected,
  onToggleSelect,
  updateFile,
  deleteFile,
  onSearch,
  loadingDelete,
  setImagePath,
}) => {
  const [isShowWarn, setIsShowWarn] = useState();
  const handleShowWarn = useCallback(() => setIsShowWarn(($) => !$), []);
  const handleDelete = useCallback(() => deleteFile({ id, fileData: file }), [deleteFile, file, id]);
  const handleUpdate = useCallback(
    (text) => {
      if (updateFile) updateFile({ ...file, [NAME]: text });
    },
    [file, updateFile]
  );
  const handleSearch = useCallback(() => onSearch(id), [id, onSearch]);
  const handleToggle = useCallback(() => {
    if (onToggleSelect) onToggleSelect(id);
  }, [id, onToggleSelect]);
  const showDelete = !readMode && Boolean(deleteFile);
  const showActions = Boolean(onSearch) || showDelete || file[OFFLINE];

  return (
    <Container>
      <PreviewWrap>
        <Preview data={file} setImageUrl={setImagePath} />
        {selected && <Overlay />}
      </PreviewWrap>
      {editable ? (
        <Input
          value={file[NAME] || file[ORIGINAL_NAME]}
          handleChangeText={handleUpdate}
          readMode={readMode || !updateFile}
          mb={0}
        />
      ) : (
        <Name>{file[NAME] || file[ORIGINAL_NAME]}</Name>
      )}
      {showActions && (
        <ButtonWrap>
          {onSearch && (
            <SmallBtn type="button" onClick={handleSearch} disabled={status !== 1}>
              {status === 0 ? <Spinner /> : <Search />}
            </SmallBtn>
          )}
          {file[OFFLINE] && (
            <IconWrap>
              <OfflineIcon />
            </IconWrap>
          )}
          {showDelete && (
            <DeleteBtn type="button" onClick={handleShowWarn}>
              <Close />
            </DeleteBtn>
          )}
        </ButtonWrap>
      )}
      {selectable && (
        <Checkbox
          selected={selected}
          onClick={handleToggle}
          readMode={readMode || !onToggleSelect}
          size={18}
          mr={0}
          mb={0}
          iconType="check"
        />
      )}
      {isShowWarn && (
        <Warn
          message="Wollen Sie die Datei wirklich löschen?"
          showActon={handleShowWarn}
          deleteAction={handleDelete}
          loading={loadingDelete}
          smallWidth
        />
      )}
    </Container>
  );
};

Item.defaultProps = {
  status: undefined,
  readMode: false,
  editable: false,
  selectable: false,
  selected: false,
  onToggleSelect: null,
  updateFile: null,
  deleteFile: null,
  onSearch: null,
  loadingDelete: false,
};

Item.propTypes = {
  file: PropTypes.shape({
    [FILE_ID]: PropTypes.string.isRequired,
    [FILE_URL]: PropTypes.string,
    [FILE_URL_EXPIRES]: PropTypes.string,
    [BUCKET]: PropTypes.string.isRequired,
    [MIMETYPE]: PropTypes.string.isRequired,
    [NAME]: PropTypes.string.isRequired,
    [ORIGINAL_NAME]: PropTypes.string.isRequired,
    [OFFLINE]: PropTypes.bool,
  }).isRequired,
  id: PropTypes.string.isRequired,
  status: PropTypes.number,
  readMode: PropTypes.bool,
  editable: PropTypes.bool,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
  updateFile: PropTypes.func,
  deleteFile: PropTypes.func,
  onSearch: PropTypes.func,
  loadingDelete: PropTypes.bool,
  setImagePath: PropTypes.func.isRequired,
};

export default Item;
